import BaseStore from './BaseStore';
import rp from 'request-promise';
import config from '../config';
import 'antd/dist/antd.css';
import { getToken } from '../components/Token';
import { getTenantHeaders } from '../components/TenantHeaders';
import { clearLocalStorage } from '../utils/common';

export class TenantStore extends BaseStore {
  constructor() {
    super();
    this.observable({
      loading: false,
      tenantIds: null,
      offset: 0,
      limit: 15,
      search: null,
      sort: null,
      order: null,
      action: null,
      displayedTenants: [],
      tenantsInfo: [],
      selected: [],
      tenantId: null,
      tenantName: '',
    });
    this.searchText = null;
    this.tenants = [];
    this.tenantsOfLocation = [];
  }

  reformatData(tenants) {
    if (tenants && tenants.data && tenants.data.length > 0) {
      const formatData = tenants.data.map((t) => {
        t.toggle = false;
        if (t.groups && t.groups.length > 0) {
          t.groups.forEach((g) => {
            if (g.subGroups && g.subGroups.length > 0) {
              g.subGroups.map((s) => {
                s.label = s.name;
                s.value = s.id;
                return s;
              });
            }
          });
        }
        return t;
      });
      return formatData;
    }
    return [];
  }

  setAction(task, data) {
    this[task] = data;
  }

  resetSelected() {
    this.selected = [];
  }

  resetData() {
    this.offset = 0;
    this.limit = 15;
    this.search = null;
    this.sort = null;
    this.order = null;
    this.tenantIds = null;
  }

  setSearch(text) {
    this.searchText = text;
  }

  async setData(task, data, getTenants = true) {
    this[task] = data;
    if (getTenants) {
      this.getTenants(this.offset, this.limit, this.search, this.sort, this.order, this.tenantIds);
    }
  }

  async getTenants(offset = 0, limit = 0, search = null, sort = null, order = null, tenantIds = null) {
    this.loading = true;
    let queryOption = {};
    queryOption.offset = offset;
    queryOption.limit = limit;
    if (tenantIds && tenantIds.length > 0) {
      queryOption.tenantIds = JSON.stringify(tenantIds);
    }
    if (search) {
      queryOption.search = search;
    }
    if (sort) {
      queryOption.sort = sort;
    }
    if (order) {
      queryOption.order = order;
    }

    try {
      let url = `${config.api.url}/${config.api.version}/tenant/cms/list`;
      const res = await rp
        .get(url, {
          qs: queryOption,
          authorization: true,
          resolveWithFullResponse: true,
          json: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res.statusCode === 200) {
        let tenants = res.body || [];
        tenants.data = this.reformatData(tenants);
        this.tenants = tenants;
        this.displayedTenants = tenants;
      }
    } catch (err) {
      if (err.statusCode === 401) {
        clearLocalStorage();
        window.location.href = '/login';
      }
      throw err;
    } finally {
      this.loading = false;
    }
  }

  async getTenantsInfo() {
    this.loading = true;
    try {
      let url = `${config.api.url}/${config.api.version}/tenant/basic/info`;
      const res = await rp
        .get(url, {
          authorization: true,
          resolveWithFullResponse: true,
          json: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res.statusCode === 200) {
        const tenants = res.body || [];
        this.tenantsInfo = tenants;
        return this.tenantsInfo;
      }
    } catch (err) {
      if (err.statusCode === 401) {
        clearLocalStorage();
        window.location.href = '/login';
      }
      throw err;
    } finally {
      this.loading = false;
    }
  }

  async getTenantsByLocationId(locationId) {
    this.loading = true;
    try {
      let url = `${config.api.url}/${config.api.version}/tenant/location/${locationId}`;
      const res = await rp
        .get(url, {
          authorization: true,
          resolveWithFullResponse: true,
          json: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res.statusCode === 200) {
        const tenants = res.body || [];
        this.tenantsOfLocation = tenants;
        return tenants;
      }
    } catch (err) {
      if (err.statusCode === 401) {
        clearLocalStorage();
        window.location.href = '/login';
      }
      throw err;
    } finally {
      this.loading = false;
    }
  }

  async getTenantsById(tenantId) {
    this.loading = true;
    try {
      let url = `${config.api.url}/${config.api.version}/tenant/${tenantId}`;
      const res = await rp
        .get(url, {
          authorization: true,
          resolveWithFullResponse: true,
          json: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res.statusCode === 200) {
        const tenant = res.body || {};
        return tenant;
      }
    } catch (err) {
      if (err.statusCode === 401) {
        clearLocalStorage();
        window.location.href = '/login';
      }
      throw err;
    } finally {
      this.loading = false;
    }
  }

  async createTenant(data) {
    this.loading = true;
    try {
      const detail = {
        name: data.name,
        locationId: data.locationId,
        usageType: data.usageType,
        address: data.address,
        district: data.district,
        province: data.province,
        postcode: data.postcode,
      };
      const res = await rp
        .post(`${config.api.url}/${config.api.version}/tenant`, {
          body: detail,
          json: true,
          authorization: true,
          resolveWithFullResponse: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res && res.statusCode === 200) {
        return res;
      }
    } catch (err) {
      throw err;
    } finally {
      this.loading = false;
    }
  }

  async updateTenant(data) {
    this.loading = true;
    try {
      const detail = {
        name: data.name,
        locationId: data.locationId,
        usageType: data.usageType,
        address: data.address,
        district: data.district,
        province: data.province,
        postcode: data.postcode,
      };
      const res = await rp
        .put(`${config.api.url}/${config.api.version}/tenant/${data.id}`, {
          body: detail,
          json: true,
          authorization: true,
          resolveWithFullResponse: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res && res.statusCode === 200) {
        return res;
      }
    } catch (err) {
      throw err;
    } finally {
      this.loading = false;
    }
  }

  async deleteTenant(id) {
    this.loading = true;
    try {
      const res = await rp
        .delete(`${config.api.url}/${config.api.version}/tenant/${id}`, {
          json: true,
          authorization: true,
          resolveWithFullResponse: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res && res.statusCode === 200) {
        return res;
      }
    } catch (err) {
      console.log('error', err);
      throw err;
    } finally {
      this.loading = false;
    }
  }
}

export default new TenantStore();
